import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AssetDeclaration } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['DECLARATION_REGISTRAR'];

/** GET /asset-declarations/:id — registrar review actions + gazette publication of the fact-of-filing only. */
export function AssetDeclarationDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isRegistrar = persona?.roles.includes('DECLARATION_REGISTRAR') ?? false;
  const declQ = useApiQuery<AssetDeclaration>(id ? `/api/v1/m40/asset-declarations/${id}` : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showFlag, setShowFlag] = useState(false);

  if (declQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading declaration…</div>;
  if (declQ.error) return <ErrorNote message={declQ.error} />;
  const d = declQ.data;
  if (!d) return null;

  const post = async (path: string, body?: unknown) => {
    setBusy(path);
    setActionError(null);
    try {
      await api.post(`/api/v1/m40/asset-declarations/${d.id}/${path}`, body);
      declQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    } finally {
      setBusy(null);
    }
  };

  const totalAssets = d.assetsJson.reduce((sum, a) => sum + a.estimatedValueScr, 0);
  const totalLiabilities = d.liabilitiesJson.reduce((sum, l) => sum + l.amountScr, 0);

  return (
    <div>
      <PageHeader title={d.refNo} subtitle={`${d.officerName} · ${d.position} (${d.mdaCode}) · FY${d.financialYear}`} actions={<StatusBadge status={d.status} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total assets" value={scr(totalAssets)} tone="good" />
        <StatCard label="Total liabilities" value={scr(totalLiabilities)} />
        <StatCard label="Filed" value={formatDate(d.submittedAt)} />
        <StatCard label="Due" value={formatDate(d.dueAt)} />
      </div>

      <div className="card mb-4 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Assets</h2>
        {d.assetsJson.length === 0 ? (
          <p className="text-xs text-slate-400">None declared.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">Description</th>
                <th className="table-head text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {d.assetsJson.map((a, i) => (
                <tr key={i}>
                  <td className="table-cell text-xs">{a.type}</td>
                  <td className="table-cell">{a.description}</td>
                  <td className="table-cell text-right tabular-nums">{scr(a.estimatedValueScr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mb-4 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Liabilities</h2>
        {d.liabilitiesJson.length === 0 ? (
          <p className="text-xs text-slate-400">None declared.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">Description</th>
                <th className="table-head text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {d.liabilitiesJson.map((l, i) => (
                <tr key={i}>
                  <td className="table-cell text-xs">{l.type}</td>
                  <td className="table-cell">{l.description}</td>
                  <td className="table-cell text-right tabular-nums">{scr(l.amountScr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Income sources</h2>
        {d.incomeSourcesJson.length === 0 ? (
          <p className="text-xs text-slate-400">None declared.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {d.incomeSourcesJson.map((inc, i) => (
              <li key={i}>
                <span className="font-medium">{inc.source}</span>
                {inc.description ? <span className="text-slate-500"> — {inc.description}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {d.reviewFlags.length > 0 ? (
        <div className="card mb-6 border-amber-200 bg-amber-50 p-4">
          <h2 className="mb-2 text-sm font-bold text-amber-800">Review flags</h2>
          <ul className="space-y-1 text-xs text-amber-800">
            {d.reviewFlags.map((f, i) => (
              <li key={i}>
                <span className="font-semibold">{f.flag}</span> — {f.note} ({formatDate(f.raisedAt)})
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span>Publication required: {d.publicationRequired ? 'Yes (senior office holder)' : 'No — filed confidentially'}</span>
        {d.docVaultReceiptSerial ? <span>DocVault receipt: {d.docVaultReceiptSerial}</span> : null}
        {d.published ? <span className="font-semibold text-emerald-700">Published (fact of filing only, never figures)</span> : null}
      </div>

      {isRegistrar ? (
        <div className="flex flex-wrap gap-2">
          {d.status !== 'CLEARED' ? (
            <button className="btn-secondary" onClick={() => setShowFlag(true)} disabled={busy !== null}>
              Raise flag
            </button>
          ) : null}
          {d.status !== 'CLEARED' ? (
            <button className="btn-success" onClick={() => post('clear')} disabled={busy !== null}>
              {busy === 'clear' ? 'Clearing…' : 'Clear'}
            </button>
          ) : null}
          {d.status !== 'CLEARED' ? (
            <button className="btn-danger" onClick={() => post('mark-non-compliant')} disabled={busy !== null}>
              {busy === 'mark-non-compliant' ? 'Marking…' : 'Mark non-compliant'}
            </button>
          ) : null}
          {d.publicationRequired && d.status === 'CLEARED' && !d.published ? (
            <button className="btn-primary" onClick={() => post('publish')} disabled={busy !== null}>
              {busy === 'publish' ? 'Publishing…' : 'Publish fact-of-filing to Gazette'}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-slate-400">Only {REGISTRAR_ROLES.join('/')} may review, flag, clear, or publish a declaration.</p>
      )}

      {showFlag ? (
        <FlagModal
          onClose={() => setShowFlag(false)}
          onSubmit={async (body) => {
            await post('flag', body);
            setShowFlag(false);
          }}
        />
      ) : null}
    </div>
  );
}

function FlagModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { flag: string; note: string }) => Promise<void> }) {
  const [flag, setFlag] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ flag, note });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to raise flag');
      setBusy(false);
    }
  };

  return (
    <Modal title="Raise a review flag" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Flag code">
          <input className="field-input" value={flag} onChange={(e) => setFlag(e.target.value)} placeholder="e.g. ASSET_VALUE_INCONSISTENT_WITH_SALARY" />
        </Field>
        <Field label="Note">
          <textarea className="field-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || flag.trim().length < 2 || note.trim().length < 2}>
            {busy ? 'Saving…' : 'Raise flag'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
